<template>
  <b-row class="Approval">
    <b-col cols="12" lg="10" offset-lg="1">
      <Notification v-bind.sync="notification" />
      <Notification v-bind.sync="forApprovalNotifOptions">
        <h5>
          <v-icon name="table" scale="1.5" />
          <span class="ml-1" v-html="approvalNotifText"></span>
          <b-link
            :to="{ name: 'for-approval' }"
            class="float-right"
          >
            back to list
            <v-icon name="angle-double-right" />
          </b-link>
        </h5>

        <div class="px-3 pt-2">
          <b-button
            size="md"
            class="rounded-0 mr-1"
            variant="outline-danger"
            @click="openConfirm('reject')"
          >
            Reject
          </b-button>
          <b-button
            size="md"
            class="rounded-0"
            variant="success"
            @click="openConfirm('approve')"
          >
            Approve
          </b-button>
        </div>
      </Notification>

      <TimesheetCard
        no-refresh
        no-submit
        :employee="userEmployeeData"
        :loadingEmployee="loadingTimesheetData"
        :timesheetData="timesheetData"
        :other-details="otherTimesheetData"
        :other-details-options="otherDetailsOptions"
      />
    </b-col>
    <Modal
      v-bind.sync="confirmModalOptions"
      @okBtn="approveOrReject"
      @cancelBtn="resetAction"
    >
      <template slot="default">
        <span v-html="confirmModalOptions.messageHtml"></span>
        <div
          v-if="approvalAction === 'reject'"
          class="row mt-5"
        >
          <b-col>
            <label class="col-form-label font-weight-bold pt-0" for="reject_reason">Comments</label>
            <b-form-textarea
              class="rounded-0"
              name="reject_reason"
              v-model="comments"
              rows="4"
              no-resize
            />
          </b-col>
        </div>
      </template>
    </Modal>
  </b-row>
</template>
<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import Modal from '@/components/Modal';
import Notification from '@/components/Notification';
import TimesheetCard from '@/components/TimesheetCard';
import TimesheetService from '@/services/TimesheetService';
import timesheetStatuses from '@/mixins/timesheetStatuses';

const timesheet = new TimesheetService();

export default {
  name: 'Approval',
  components: {
    Modal,
    Notification,
    TimesheetCard,
  },
  mixins: [timesheetStatuses],
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      const { key } = vm.$route.params;
      const { id } = vm.userData;

      vm.loadingTimesheetData = true;

      timesheet.checkIfApprover(id, key)
        .then(({ success, message, isApprover }) => {
          if (!success || !isApprover) {
            vm.setAlert({
              show: 5,
              variant: 'danger',
              fade: true,
              dismissible: true,
              message: typeof message === 'string' ? message : 'An error occurred',
              icon: {
                name: 'exclamation',
              },
            });
          }
          return success;
        })
        .catch(() => {
          vm.setAlert({
            show: 5,
            variant: 'danger',
            fade: true,
            dismissible: true,
            message: 'An error occurred',
            icon: {
              name: 'exclamation',
            },
          });

          return false;
        })
        .then((success) => {
          if (!success) {
            return vm.$router.push({ name: 'home' });
          }

          // User is the verified approver, get timesheet data
          vm.startLoadingRequest();
          vm.startLoadingPage();
          return timesheet.getTimesheetByKey(key);
        })
        .then(vm.displayData)
        .then(() => {
          vm.stopLoadingRequest();
          vm.stopLoadingPage();
          vm.loadingTimesheetData = false;
        });
    });
  },
  data() {
    return {
      comments: '',
      loadingApproveReject: false,
      loadingTimesheetData: false,
      userEmployeeData: {},
      otherTimesheetData: {},
      timesheetData: [],
      timesheetStatus: {},
      approvalAction: '',
      confirmModalOptions: {
        show: false,
      },
    };
  },
  computed: {
    approvalActionVerb() {
      const { status_id: id } = this.timesheetStatus;
      switch (id) {
        case this.pendingApproval:
          return 'revert to pending status';
        case this.approved:
          return 'success';
        case this.rejected:
          return 'danger';
        default:
          return '';
      }
    },
    statusVariant() {
      const { status_id: id } = this.timesheetStatus;
      switch (id) {
        case this.pendingApproval:
          return 'warning';
        case this.approved:
          return 'success';
        case this.rejected:
          return 'danger';
        default:
          return 'light';
      }
    },
    forApprovalNotifOptions() {
      // Do not show for approval notif when timesheet still loading or already approved/rejected
      const { status_id: id } = this.timesheetStatus;
      if (this.isLoadingRequest || id !== this.pendingApproval) {
        return {
          show: false,
        };
      }
      return {
        show: true,
        variant: 'light',
      };
    },
    otherDetailsOptions() {
      const defaultOptions = {
        size: 'sm',
        readonly: true,
        modifiers: ['text-left-align'],
        options: {
          inputCol: true,
          labelColsLg: 4,
          labelColsSm: 4,
          labelCols: 12,
          inputCols: 12,
          inputColsSm: 8,
          inputColsLg: 7,
        },
      };
      const { status } = this.timesheetStatus;
      const currentStatus = this.$startCase(status);
      const fields = [
        {
          ...defaultOptions,
          key: 'cutoffPeriodFormatted',
          label: 'Cutoff Period',
          value: '',
        },
        {
          ...defaultOptions,
          label: 'Status',
          html: `<span class="badge badge-${this.statusVariant}">${currentStatus}</span>`,
        },
      ];

      return { fields, labelClass: 'font-weight-bold' };
    },
    approvalNotifText() {
      const { full_name: name } = this.userEmployeeData;
      return `Timesheet for <strong class="text-uppercase">${name}</strong> requires your immediate attention`;
    },
    ...mapState(['notification']),
    ...mapGetters([
      'userData',
      'isLoadingRequest',
      'isLoadingPage',
    ]),
  },
  methods: {
    displayData(result) {
      if (typeof result === 'object' && result) {
        // Set the data for display
        const { user, timesheetStatus, timesheet: tsData } = result;
        const { data, ...otherData } = tsData;

        // format cutoff dates
        const cutoffDates = this.$get(otherData, 'cutoff_period', []);
        const cutoffPeriodFormatted = cutoffDates.map((codate, idx) => {
          const pattern = idx === 0 ? 'MMM DD' : 'DD YYYY';
          return this.$fd(codate, pattern);
        }).join(' - ');

        this.userEmployeeData = user;
        this.timesheetData = data;
        this.timesheetStatus = timesheetStatus;
        this.otherTimesheetData = Object.assign({}, { cutoffPeriodFormatted }, otherData);
      }
    },
    openConfirm(action) {
      const formattedAction = this.$startCase(action);
      const { full_name: name } = this.userEmployeeData;
      const title = `${formattedAction} Timesheet`;
      const messageHtml = `Are you sure you want to ${action} the timesheet for <strong class="text-uppercase">${name}</strong>?`;

      // set approval action
      this.approvalAction = action;

      let okBtnVariant = '';
      switch (action) {
        case 'approve':
          okBtnVariant = 'success';
          break;
        case 'reject':
          okBtnVariant = 'danger';
          break;
        default:
          okBtnVariant = 'primary';
          break;
      }

      this.confirmModalOptions = {
        messageHtml,
        title,
        show: true,
        cancelBtnOptions: {
          variant: 'outline-secondary',
        },
        okBtnOptions: {
          size: 'lg',
          variant: okBtnVariant,
          text: formattedAction,
        },
      };
    },
    approveOrReject() {
      const action = this.approvalAction;
      if (!action) {
        // TODO: approval action not set
        return this.setAlert({
          show: 5,
          dismissible: true,
          variant: 'danger',
          message: 'Client error',
        });
      }
      const { key } = this.$route.params;
      const { id: approverId } = this.userData;

      this.loadingApproveReject = true;
      this.startLoadingPage();
      // Execute approval (or rejection) of timesheet
      return timesheet.updateApprovalStatus(action, key, approverId, this.comments)
        .then(({ success, message }) => {
          const defaultMessage = success ? 'Timesheet updated.' : 'An error occurred';

          this.setNotification({
            message: message || defaultMessage,
            show: true,
            dismissible: true,
            refresh: !success,
            variant: success ? 'success' : 'danger',
            icon: {
              name: success ? 'check' : 'exclamation-triangle',
            },
          });

          if (success) {
            // update timesheet status
            switch (action) {
              case 'approve':
                this.timesheetStatus = {
                  status_id: this.approved,
                  status: 'approved',
                };
                break;
              case 'reject':
                this.timesheetStatus = {
                  status_id: this.rejected,
                  status: 'rejected',
                };
                break;
              default: break;
            }
          }
        })
        .catch(() => {
          this.setNotification({
            show: true,
            dismissible: true,
            refresh: true,
            variant: 'danger',
            message: 'An error occurred',
            icon: {
              name: 'exclamation-triangle',
            },
          });
        })
        .then(() => {
          this.loadingApproveReject = false;
          this.stopLoadingPage();
        });
    },
    resetAction() {
      this.approvalAction = '';
    },
    ...mapActions([
      'setAlert',
      'resetAlert',
      'setNotification',
      'startLoadingRequest',
      'stopLoadingRequest',
      'startLoadingPage',
      'stopLoadingPage',
    ]),
  },
};
</script>
